import { Link, useParams, Navigate } from "react-router-dom";
import { ArrowUpRight, Check } from "lucide-react";
import SEO from "../components/SEO";
import CTAStrip from "../components/CTAStrip";
import { PRODUCTS } from "../data/products";
import { PRODUCTS_EN } from "../data/products_en";
import { useLang } from "../i18n/LanguageContext";

export default function ProductPage() {
    const { slug } = useParams();
    const { lang, t } = useLang();
    const path = window.location.pathname.replace(/^\//, "");
    const productSlug = slug || path;
    const productFr = PRODUCTS[productSlug];
    if (!productFr) return <Navigate to="/portails" replace />;

    const productEn = PRODUCTS_EN[productSlug] || {};
    const product = lang === "en"
        ? { ...productFr, ...productEn }
        : productFr;

    return (
        <>
            <SEO title={product.seoTitle} description={product.seoDescription} keywords={product.keywords} image={product.image} />

            <section className="relative">
                <div className="absolute inset-0" aria-hidden>
                    <img src={product.image} alt="" className="w-full h-full object-cover opacity-25" />
                    <div className="absolute inset-0 bg-gradient-to-b from-[#0A1118]/85 to-[#0A1118]" />
                </div>
                <div className="relative max-w-[1400px] mx-auto px-6 lg:px-10 pt-20 pb-20">
                    <div className="divider-tag mb-6">{product.category}</div>
                    <h1 className="font-display text-4xl md:text-6xl text-white leading-[1.05] tracking-tight max-w-4xl">
                        {product.title}
                    </h1>
                    <p className="mt-7 text-white/70 max-w-2xl text-lg leading-relaxed">{product.intro}</p>
                    <div className="mt-9 flex gap-4 flex-wrap">
                        <Link to="/contact" className="btn-primary">{t("cta.quote")}</Link>
                        <Link to="/realisations" className="btn-ghost">{t("cta.see_projects")} <ArrowUpRight className="w-4 h-4" /></Link>
                    </div>
                </div>
            </section>

            <section className="max-w-[1400px] mx-auto px-6 lg:px-10 py-20 md:py-28 grid md:grid-cols-12 gap-12">
                <div className="md:col-span-7 order-2 md:order-1">
                    <div className="divider-tag mb-5">{t("product.characteristics")}</div>
                    <h2 className="font-display text-3xl md:text-4xl text-white mb-10">{t("product.approach")} — {product.title.toLowerCase()}</h2>
                    <div className="grid sm:grid-cols-2 gap-px bg-white/[0.07] border border-white/[0.07]">
                        {product.bullets.map((b) => (
                            <div key={b.t} className="bg-[#0B141C] p-7">
                                <div className="flex items-center gap-2 mb-3">
                                    <Check className="w-4 h-4 text-brand-accent" />
                                    <div className="text-white font-medium">{b.t}</div>
                                </div>
                                <p className="text-sm text-white/60 leading-relaxed">{b.d}</p>
                            </div>
                        ))}
                    </div>

                    <div className="mt-12">
                        <div className="divider-tag mb-4">{t("product.keywords")}</div>
                        <div className="flex flex-wrap gap-2">
                            {product.keywords.map((k) => (
                                <span key={k} className="text-xs px-3 py-1.5 bg-white/[0.04] border border-white/[0.08] text-white/65">{k}</span>
                            ))}
                        </div>
                    </div>
                </div>

                <div className="md:col-span-5 order-1 md:order-2">
                    <div className="relative aspect-[4/5] overflow-hidden border border-white/10">
                        <img src={product.image} alt={product.title} className="w-full h-full object-cover" />
                    </div>
                    <div className="mt-6 text-xs text-white/45">
                        {t("product.visit_note")}
                    </div>
                </div>
            </section>

            <CTAStrip
                title={lang === "en" ? `Your ${product.title.toLowerCase()} project?` : `Votre projet ${product.title.toLowerCase()} ?`}
                subtitle={lang === "en" ? "Tell us about your entrance, your site and your constraints. We'll propose a tailored, priced solution." : "Décrivez-nous votre entrée, votre terrain et vos contraintes. Nous vous proposons une solution adaptée et chiffrée."}
            />

            <section className="max-w-[1400px] mx-auto px-6 lg:px-10 py-20 md:py-28">
                <div className="divider-tag mb-5">{t("product.explore")}</div>
                <h2 className="font-display text-3xl md:text-4xl text-white mb-10">{t("product.explore_title")}</h2>
                <div className="grid sm:grid-cols-2 lg:grid-cols-4 gap-4">
                    {Object.values(PRODUCTS).filter((p) => p.slug !== product.slug).slice(0, 4).map((p) => {
                        const pEn = PRODUCTS_EN[p.slug] || {};
                        const merged = lang === "en" ? { ...p, ...pEn } : p;
                        return (
                            <Link key={p.slug} to={`/${p.slug}`} className="card-product block group">
                                <div className="img-wrap aspect-[4/3]">
                                    <img src={merged.image} alt={merged.title} className="w-full h-full object-cover" loading="lazy" />
                                </div>
                                <div className="p-5">
                                    <div className="text-[10px] tracking-[0.28em] uppercase text-brand-accent/80 mb-1">{merged.category}</div>
                                    <div className="font-display text-xl text-white group-hover:text-brand-accent transition">{merged.title}</div>
                                </div>
                            </Link>
                        );
                    })}
                </div>
            </section>
        </>
    );
}
